import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import classnames from 'classnames';

import lodashGet from 'lodash/get';

import { hasRoleAnonymous } from 'common/userRoles';
import { SR_API_ACTION_GET_LISTING_LIST } from 'api/actionTypes';

import analytics from 'client/analytics';
import {
  ANALYTICS_EVENT_MAP_RELOADED,
} from 'client/analytics/eventList';

import SrSwipe from 'client/components/SrSwipe';
import { SCREEN_ID_LISTINGS_MAP } from 'client/constants/screenIds';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withApiState from 'client/helpers/withApiState';

import Navbar from 'client/components/Navbar';
import NavbarButton from 'client/components/NavbarButton';
import SrListingsMapView from 'client/components/SrListingsMapView';
import ListingCard from 'client/components/ListingCard';
import TabBar from 'client/components/TabBar';
import SrResultsMatchedCard from 'client/components/SrResultsMatchedCard';
import SrProgressBarPercent from 'client/components/SrProgressBarPercent/SrProgressBarPercent';
import SrContinueApplicationButton from 'client/components/SrContinueApplicationButton';
import SrSuggestedSearchRemove from 'client/components/SrSuggestedSearchRemove';

import ListingActions from 'client/actions/Listing';
import HouseholdActions from 'client/actions/Household';
import SearchActions from 'client/actions/Search';

import branch from 'client/core/branch';

export const translate3d = (x, y) => {
  const translate = `translate3d(${x}px, ${y}px, 0px)`;
  return {
    msTransform: translate,
    WebkitTransform: translate,
    transform: translate,
  };
};

@withTransitionHook
@withScreenId(SCREEN_ID_LISTINGS_MAP)
@withScrollToTop
@withApiState({
  listingsApiState: {
    action: SR_API_ACTION_GET_LISTING_LIST,
    queryId: 'map',
  },
})
@branch({
  currentUser: ['currentUser'],
  bookmarksByListingId: ['bookmarksByListingId'],
  listings: ['listings', 'records'],
  totalListingsCount: ['listings', 'totalListingsCount'],
  viewingListingId: ['listingsMapView', 'viewingListingId'],
  showRedoSearch: ['listingsMapView', 'showRedoSearch'],
  showResultsMatched: ['listingsMapView', 'showResultsMatched'],
  listingsTotal: ['listings', 'total'],
  householdApplications: ['householdApplications'],
  listingPhotoResolution: ['listingPhotoResolution'],
  renterProfilesByUserId: ['household', 'renterProfilesByUserId'],
  selectedSuggestedSearchFilter: ['search', 'selectedSuggestedSearchFilter'],
})
@withRouter
class ListingsMap extends Component {
  static fetchData(state, inject) {
    const currentUser = inject.state.get(['currentUser']);
    const isAnonymous = hasRoleAnonymous(currentUser.roles);
    const noRecords = inject.state.get(['listings', 'records']).length === 0;
    return Promise.all([
      noRecords && ListingActions.getTotalListingsCount(inject),
      !isAnonymous && HouseholdActions.loadRenterProfile(inject, { userId: currentUser.id }),
    ]);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Rent the best homes in Texas | Sunroom Rentals',
    section: 'Listings',
    keywords: 'rent, home, austin, tour, apartment',
    description: (
      'Spend less time scheduling tours, our rental experts will schedule ' +
      'and confirm tours for you.'
    ),
    image: 'https://sunroomrentals.com/images/metadata/og-house.jpg',
  });

  static propTypes = {
    listings: PropTypes.array.isRequired,
    currentUser: PropTypes.object.isRequired,
    viewingListingId: PropTypes.number,
    history: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    totalListingsCount: PropTypes.number,
    showRedoSearch: PropTypes.bool.isRequired,
    showResultsMatched: PropTypes.bool,
    listingsApiState: PropTypes.object.isRequired,
    householdApplications: PropTypes.array,
    listingPhotoResolution: PropTypes.number.isRequired,
    renterProfilesByUserId: PropTypes.object,
    bookmarksByListingId: PropTypes.object,
    selectedSuggestedSearchFilter: PropTypes.object,
  };

  constructor() {
    super();

    this.state = {
      isMobile: false,
      scrollY: 0,
    };
  }

  componentDidMount() {
    this._setBrowserWidthInState();
    global.document.body.addEventListener('touchmove', this._preventDefault, { passive: false });

    this.props.dispatch(({ state }) => {
      state.set(['listingsViewMode'], 'map');
    });
  }

  componentDidUpdate(prevProps) {
    this._shouldShowResultsMatched(prevProps);
  }

  componentWillUnmount() {
    global.document.body.removeEventListener('touchmove', this._preventDefault, { passive: false });
  }

  _shouldShowResultsMatched = (prevProps) => {
    if (prevProps.listings.length === 0 &&
      this.props.listings.length &&
      this.props.location.search.includes('signup')
    ) {
      this.props.dispatch(SearchActions.setMapViewValue, 'showResultsMatched', true);
    }
  }

  _setBrowserWidthInState = () => {
    const browserWidth = global.innerWidth
      || lodashGet('documentElement.clientWidth', global.document)
      || lodashGet('body.clientWidth', global.document);

    this.setState({
      isMobile: browserWidth < 700,
    });
  }

  _preventDefault = (e) => {
    e.preventDefault();
  }

  _onSwipeEnd = () => {
    const { dispatch } = this.props;
    const { scrollY } = this.state;
    const deltaY = 70;

    if (scrollY > deltaY) {
      dispatch(({ state }) => {
        state.set(['listingsMapView', 'viewingListingId'], null);
        setTimeout(() => {
          this.setState({ scrollY: 0 });
        }, 300);
      });
    }
  }

  _onSwipe = (e) => {
    this.setState({
      scrollY: e[1] < 0 ? 0 : e[1],
    });
  }

  _onBack = () => {
    const { history, dispatch } = this.props;
    dispatch(({ state }) => {
      state.set(['listingsViewMode'], 'list');
    });
    history.push('/feed');
  }

  _onRedoSearch = () => {
    analytics.track(ANALYTICS_EVENT_MAP_RELOADED);
    this.props.dispatch(SearchActions.setMapViewValue, 'showRedoSearch', false);
  }

  _removeSuggestedFilter = () => {
    this.props.dispatch(SearchActions.removeSuggestedFilter);
  }

  render() {
    const {
      location,
      history,
      listings,
      currentUser,
      viewingListingId,
      totalListingsCount,
      listingsTotal,
      showRedoSearch,
      listingsApiState,
      showResultsMatched,
      householdApplications,
      listingPhotoResolution,
      renterProfilesByUserId,
      bookmarksByListingId,
      selectedSuggestedSearchFilter,
    } = this.props;

    const {
      isMobile,
      scrollY,
    } = this.state;

    const viewingListing = viewingListingId
      ? listings.find((listing) => listing.id === viewingListingId)
      : false;

    const isAnonymous = hasRoleAnonymous(currentUser.roles);

    const viewingListingApplication = (
      viewingListing
      && householdApplications
      && householdApplications.find(({ listingId }) => listingId === viewingListing.id)
    );

    const renterProfile = renterProfilesByUserId && renterProfilesByUserId[currentUser.id] || null;

    return (
      <div
        className={classnames(
          'page-explore',
          { anonymous: isAnonymous },
        )}
      >
        {selectedSuggestedSearchFilter &&
          <SrSuggestedSearchRemove
            displayName={selectedSuggestedSearchFilter.displayName}
            removeSuggestedFilter={this._removeSuggestedFilter}
          />
        }
        <div className='mapView'>
          <div className='tipsVariation'>
            <Navbar
              leftButton={<NavbarButton onClick={() => history.push('/search')}>Filter</NavbarButton>}
              rightButton={(
                <NavbarButton onClick={this._onBack}>List</NavbarButton>
              )}
            />
          </div>

          {!isAnonymous && (
            <SrContinueApplicationButton />
          )}

          {!isAnonymous && (
            <TabBar />
          )}

          <div className='mapContainer'>
            <div className='innerMapContainer'>
              <SrListingsMapView
                listings={listings}
                isMobile={isMobile}
                location={location}
                bookmarksByListingId={Object.keys(bookmarksByListingId || {})}
              />
            </div>
          </div>

          {showResultsMatched && (
            <div className='floatingAlert resultsMatched animated'>
              <SrResultsMatchedCard
                resultsCount={listingsTotal}
                totalListingsCount={totalListingsCount}
              />
            </div>
          )}

          {!showResultsMatched && isMobile && showRedoSearch && (
            <div className='floatingAlert redoSearch' onClick={this._onRedoSearch}>
              <span className='headline'>search this area</span>
            </div>
          )}

          {isMobile && (
            <SrProgressBarPercent positionFixed percent={listingsApiState.progressPercent} />
          )}

          {viewingListing && (
            <div className='swipeContainerMap'>
              <SrSwipe onSwipeEnd={this._onSwipeEnd} onSwipe={this._onSwipe}>
                <div
                  style={{ draggable: true }}
                  className='listingCardContainerMap'
                >
                  <ListingCard
                    enableBookmarkButton
                    style={{ ...translate3d(0, scrollY) }}
                    listing={viewingListing}
                    currentUser={currentUser}
                    applicationStatus={viewingListingApplication && viewingListingApplication.status}
                    photoResolution={listingPhotoResolution}
                    renterProfile={renterProfile}
                  />
                </div>
              </SrSwipe>
            </div>
          )}
        </div>
      </div>
    );
  }
}

ListingsMap.displayName = 'ListingsMap';

export default ListingsMap;
